import React from 'react';

import HelpPageFooterNotice from '../components/helpPageFooterNotice';

const EditTripGroupHelp: React.FC = () => {
  return (
    <>
      <h1>Edit Trip Group</h1>
      <p>
        This page is used to view or edit the configuration rules associated
        with a specific Trip Group within your account.
      </p>
      <h2>Information</h2>
      <p>
        <strong>Lineage:</strong> This displays the full lineage of a Trip Group
        that has been created in your account. At a minimum this will include
        the CUSTOMER_ID value but may contain additional attributes depending on
        your account setup, such as VIP_FLAG, COUNTRY_ID, PCC, etc...
      </p>
      <p>
        <strong>Name:</strong> This is the name of Trip Group displayed in the
        Trip Center UI. By default, this will be the CUSTOMER_ID value assigned
        to a Trip Group when imported into your account. You can update this to
        a user-friendly name by editing the value in this field.
      </p>
      <p>
        <strong>Customize Trip Group Settings:</strong> By default, Trip Groups
        will inherit the settings for your account and cannot be adjusted unless
        you click the switch next to “Customize Trip Group Settings”. Once a
        Trip Group has been customized, it cannot be changed back to inherit the
        default settings for your account.
      </p>
      <p>
        <strong>Trip Assist: </strong>Trip Assist is a workflow management tool
        that is used to proactively manage detected potential disruptions
        affecting a trip, including cancelled flights, potential connection
        issues, actual connection issues, diversions, or excessive flight
        delays.
      </p>
      <p>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://helpdesk.cirium.com/hc/en-us/articles/360032485652-What-are-some-best-practices
        for-managing-issues-with-Trip-Assist"
        >
          https://helpdesk.cirium.com/hc/en-us/articles/360032485652-What-are-some-best-practices
          for-managing-issues-with-Trip-Assist
        </a>
      </p>
      <p>
        Trip Assist enables the user to record actions taken to resolve issues
        that are used in the Trip Assist Reports, that helps the Customer to
        understand the issues that impacted their traveler’s trip.
      </p>
      <p>
        <strong>Create Issues:</strong> To turn on the creation of Issues in
        Trip Assist for your account or an individual Trip Group, you’ll need to
        click the slider next to “Create Issues”. When turned on, the slider
        will be to the right and will be green in color. To turn off this
        feature, the click the slider to left and the display will be gray.{' '}
      </p>

      <p>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://helpdesk.cirium.com/hc/en-us/articles/217614548-What-are-the-issue-types"
        >
          https://helpdesk.cirium.com/hc/en-us/articles/217614548-What-are-the-issue-types
        </a>
      </p>
      <p>
        <strong>Create Invalid Trip Issues:</strong> This allows you to turn on
        or off the creation of Issues related to invalid or not monitorable
        trips. Once an issue is created, it can be tracked from Trip Assist
        feature in Trip Center. Issues can be resolved from Trip Assist or auto
        resolved when the trip is corrected, or the trip becomes monitorable.
      </p>
      <p>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://helpdesk.cirium.com/hc/en-us/articles/360017892412-What-makes-an-itinerary-
          invalid-or-not-monitorable"
        >
          https://helpdesk.cirium.com/hc/en-us/articles/360017892412-What-makes-an-itinerary-
          invalid-or-not-monitorable
        </a>
      </p>
      <p>
        <strong>Require Opted In Traveler Profiles For Issue Creation:</strong>{' '}
        When set, issues will only be created when the trip is configured to
        send Trip Alert messages to at least one Traveler Profile with at least
        one active contact point.
      </p>
      <p>
        <strong>Agent Trip Alerts:</strong> This is an email notification
        service that provides email-based alerts to designees when a potential
        disruption is detected, such as cancelled flights, estimated connection
        issues, actual connection issues, diversions, or excessive flight
        delays. To turn on these alerts for your account or an individual Trip
        Group, you’ll need to click the slider next to “Agent Trip Alerts”.
      </p>
      <p>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://helpdesk.cirium.com/hc/en-us/articles/360039814971-How-do-I-add-an-Agent-Trip-
        Alert-Email"
        >
          https://helpdesk.cirium.com/hc/en-us/articles/360039814971-How-do-I-add-an-Agent-Trip-
          Alert-Email
        </a>
      </p>
      <p>
        <strong>Traveler Trip Alerts:</strong> Traveler Trip Alerts is a
        notification service to provide email-based alerts to travelers that
        have opted-in to Cirium’s Traveler Trip Alert Profile system. The
        standard set of emails provided are for trip reminders, scheduled status
        updates, delays, gate changes, connection information, and flight
        cancellations. To turn on these alerts for your account or an individual
        Trip Group, you’ll need to click the slider next to “Traveler Trip
        Alerts”.
      </p>
      <p>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://helpdesk.cirium.com/hc/en-us/articles/360060870252-How-are-Traveler-Profiles-
          created"
        >
          https://helpdesk.cirium.com/hc/en-us/articles/360060870252-How-are-Traveler-Profiles-
          created
        </a>
      </p>
      <p>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://helpdesk.cirium.com/hc/en-us/articles/360035928591-How-to-enable-Traveler-Trip-
          Alerts-at-the-Trip-Group-level"
        >
          https://helpdesk.cirium.com/hc/en-us/articles/360035928591-How-to-enable-Traveler-Trip-
          Alerts-at-the-Trip-Group-level
        </a>
      </p>
      <p>
        <strong>Trip Reminders Enabled:</strong> This feature notifies the
        recipient 24 hours before their scheduled trip containing the status of
        their trip. When available, the Trip Reminder alert will also include
        the operating airline’s check-in, terms of carriage, and baggage policy.
        To turn on these alerts for your account or an individual Trip Group,
        you’ll need to click the slider next to “Trip Reminders Enabled”.
      </p>

      <p>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://helpdesk.cirium.com/hc/en-us/articles/360035167091-Why-did-my-traveler-receive-a-
          Trip-Reminder"
        >
          https://helpdesk.cirium.com/hc/en-us/articles/360035167091-Why-did-my-traveler-receive-a-
          Trip-Reminder
        </a>
      </p>
      <p>
        <a
          target="_blank"
          rel="noreferrer"
          href="https://helpdesk.cirium.com/hc/en-us/articles/21259092663067-How-do-I-enable-or-disable-
          Trip-Reminders"
        >
          https://helpdesk.cirium.com/hc/en-us/articles/21259092663067-How-do-I-enable-or-disable-
          Trip-Reminders
        </a>
      </p>
      <HelpPageFooterNotice />
    </>
  );
};
export default EditTripGroupHelp;
